import { TellescopeModelFromName, useTellescopeMutation } from "@tellescope/tanstack-query";
import { DragEndEvent, DragMoveEvent, DragStartEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { useCallback, useState } from "react";
import { addDays } from "date-fns/addDays";
import { fromZonedTime } from "date-fns-tz";
import { DAY_COLUMN, DRAG_ACTIVATION_DISTANCE_PX, MIN_DURATION_MINUTES, SNAP_INTERVAL_MINUTES } from "../constants";
import { suppressNextClick } from "../suppress-next-click";
import { useDateFnsFormattersWithLocale } from "@tellescope/ui";
import { useUnavailableUsersGuard } from "../../hooks";

type CalendarEvent = TellescopeModelFromName<"calendar_events">;

const getAttendeeUserIds = (event: CalendarEvent) =>
  event.attendees.reduce<string[]>((acc, attendee) => {
    if (attendee.type === "user") acc.push(attendee.id);
    return acc;
  }, []);

export type DayColumnDragData = { type: "move"; event: CalendarEvent } | { type: "resize"; event: CalendarEvent };

export type DayColumnDroppableData = { dayIndex: number };

export interface ActiveDayColumnDrag {
  type: "move" | "resize";
  event: CalendarEvent;
  previewMinutes: number;
  deltaMinutes: number;
}

const snapMinutes = (minutes: number) => Math.round(minutes / SNAP_INTERVAL_MINUTES) * SNAP_INTERVAL_MINUTES;
const pixelsToMinutes = (pixels: number) => pixels / DAY_COLUMN.MINUTE_HEIGHT;

export interface UseDayColumnDragLogicProps {
  startDate: Date;
  timezone: string;
}

export const useDayColumnDragLogic = ({ startDate, timezone }: UseDayColumnDragLogicProps) => {
  const { formatInTimeZone } = useDateFnsFormattersWithLocale();
  const [activeDrag, setActiveDrag] = useState<ActiveDayColumnDrag | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE_PX },
    })
  );

  const updateEventMutation = useTellescopeMutation({
    model: "calendar_events",
    method: "updateOne",
    optimisticUpdates: true,
    autoCacheUpdateStrategy: "update",
  });

  const { guard: guardUpdate, warningDialogProps: updateWarningDialogProps } = useUnavailableUsersGuard({ timezone });

  const handleDragStart = useCallback((e: DragStartEvent) => {
    const data = e.active.data.current;
    if (!data) return;
    const durationInMinutes = data.event.actualDuration ?? data.event.durationInMinutes;
    setActiveDrag({ type: data.type, event: data.event, previewMinutes: durationInMinutes, deltaMinutes: 0 });
  }, []);

  const handleDragMove = useCallback((e: DragMoveEvent) => {
    const data = e.active.data.current;
    if (!data) return;
    const deltaMinutes = snapMinutes(pixelsToMinutes(e.delta.y));
    if (data.type === "resize") {
      const baseDuration = data.event.actualDuration ?? data.event.durationInMinutes;
      const next = Math.max(MIN_DURATION_MINUTES, baseDuration + deltaMinutes);
      setActiveDrag((prev) => (prev ? { ...prev, previewMinutes: next, deltaMinutes } : prev));
      return;
    }
    setActiveDrag((prev) => (prev ? { ...prev, deltaMinutes } : prev));
  }, []);

  const handleDragEnd = useCallback(
    (e: DragEndEvent) => {
      const data = e.active.data.current;
      setActiveDrag(null);
      suppressNextClick();
      if (!data) return;

      if (data.type === "move") {
        const overData = e.over?.data.current;
        if (!overData) return;

        const [h = 0, m = 0] = formatInTimeZone(new Date(data.event.startTimeInMS), timezone, "H:m")
          .split(":")
          .map(Number);
        const minutesIntoDay = h * 60 + m;

        const deltaMinutes = snapMinutes(pixelsToMinutes(e.delta.y));
        const newMinutesIntoDay = Math.max(0, snapMinutes(minutesIntoDay + deltaMinutes));

        const targetDateStr = formatInTimeZone(addDays(startDate, overData.dayIndex), timezone, "yyyy-MM-dd");
        const newDayStartMs = fromZonedTime(`${targetDateStr} 00:00:00`, timezone).getTime();
        const newStart = newDayStartMs + newMinutesIntoDay * 60000;

        if (newStart === data.event.startTimeInMS) return;

        guardUpdate(
          {
            userIds: getAttendeeUserIds(data.event),
            start: new Date(newStart),
            durationInMinutes: data.event.actualDuration ?? data.event.durationInMinutes,
          },
          () =>
            updateEventMutation.mutate({
              id: data.event.id,
              startTimeInMS: newStart,
            })
        );
        return;
      }

      if (data.type === "resize") {
        const baseDuration = data.event.actualDuration ?? data.event.durationInMinutes;
        const deltaMinutes = snapMinutes(pixelsToMinutes(e.delta.y));
        const newDuration = Math.max(MIN_DURATION_MINUTES, baseDuration + deltaMinutes);
        if (newDuration === baseDuration) return;

        guardUpdate(
          {
            userIds: getAttendeeUserIds(data.event),
            start: new Date(data.event.startTimeInMS),
            durationInMinutes: newDuration,
          },
          () =>
            updateEventMutation.mutate({
              id: data.event.id,
              durationInMinutes: newDuration,
            })
        );
      }
    },
    [formatInTimeZone, startDate, timezone, updateEventMutation, guardUpdate]
  );

  const handleDragCancel = useCallback(() => {
    setActiveDrag(null);
    suppressNextClick();
  }, []);

  return {
    sensors,
    activeDrag,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    handleDragCancel,
    updateWarningDialogProps,
  };
};
